/**
 * WelcomeModal — greets a user the first time they land on the home page
 * after finishing the onboarding wizard (Figma MVP-DESIGNES 9370:2828).
 *
 * Layout (top to bottom): full-bleed artwork block, centered title and body
 * copy, a single centered "Get Started" button. Both that button and the X
 * only close the modal — there is nothing else to do from here.
 *
 * The artwork is the flat grey placeholder block from the design (600 × 322)
 * with a large yellow star that pops out above the top edge of the modal
 * (Figma: 396 × 396 at left 102 / top −58). The star is a decorative SVG
 * (``welcome-star.svg``) rendered as a sibling of the Spark Modal inside the
 * portal dialog and positioned absolutely against it — the dialog shares the
 * modal's top-left corner, so Figma's modal-relative offsets apply as-is.
 * Living outside the modal keeps the modal's own corner clip and its body
 * overflow measurement untouched; only the portal dialog's clip is lifted in
 * ``WelcomeModal.css`` so the part of the star above the modal stays visible.
 *
 * Because the star overhangs the box, the portal is also given extra top
 * padding (navbar + overhang) in ``WelcomeModal.css`` so the star + modal
 * composite is what gets centered below the navbar — Spark alone would
 * center the box and let the star tip run into the navbar on laptop heights.
 */
import React from 'react';

import { Modal, SparkModalPortal } from '@spark';

import WelcomeStar from '@/shared/assets/illustrations/welcome-star.svg?react';

import './WelcomeModal.css';

export const WELCOME_MODAL_WIDTH = 600;
export const WELCOME_MODAL_TITLE = 'Welcome to the SLM Revolution!';
export const WELCOME_MODAL_BODY = [
    'Get ready to experience zero-latency, hyper-focused Agentic AI.',
    'You now have the power to deploy ultra-efficient Small Language Models tailored exactly to your workflows. Let’s build something incredibly fast.',
].join('\n');
export const WELCOME_MODAL_CTA = 'Get Started';

export default function WelcomeModal({ open = false, onClose }) {
    const close = () => onClose?.();
    return (
        <SparkModalPortal
            open={open}
            onClose={close}
            width={WELCOME_MODAL_WIDTH}
            className="welcome-modal-portal"
            destroyOnClose
        >
            <Modal
                open
                className="welcome-modal"
                title={WELCOME_MODAL_TITLE}
                subtitle={WELCOME_MODAL_BODY}
                showOverlay={false}
                showScrollBar={false}
                showTertiaryButton={false}
                fixedHeight={false}
                primaryLabel={WELCOME_MODAL_CTA}
                onPrimaryClick={close}
                onCloseClick={close}
            >
                <div className="welcome-modal__artwork" data-testid="welcome-modal-artwork" aria-hidden="true" />
            </Modal>
            <WelcomeStar
                className="welcome-modal__star"
                data-testid="welcome-modal-star"
                aria-hidden="true"
                focusable="false"
            />
        </SparkModalPortal>
    );
}
